import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Close as CloseIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { TemplateVersion } from '../../../types/notification-admin';
import { NotificationPriority } from '../../../types/user';
import { notificationTemplateService } from '../../../services/notificationTemplateService';

interface TemplateVersionHistoryProps {
  open: boolean;
  templateId: string | null;
  templateType?: string;
  onClose: () => void;
  onRestore: (versionId: string) => Promise<void>;
}

/**
 * TemplateVersionHistory Component
 * Displays version history for a notification template with restore capability
 */
export const TemplateVersionHistory: React.FC<TemplateVersionHistoryProps> = ({
  open,
  templateId,
  templateType,
  onClose,
  onRestore,
}) => {
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  /**
   * Load version history when dialog opens
   */
  useEffect(() => {
    if (open && templateId) {
      loadVersionHistory();
    }
  }, [open, templateId]);

  /**
   * Load version history from service
   */
  const loadVersionHistory = async () => {
    if (!templateId) return;

    setLoading(true);
    setError(null);

    try {
      const history = await notificationTemplateService.getTemplateVersions(templateId);
      setVersions(history);
    } catch (err: any) {
      console.error('Error loading version history:', err);
      setError(err.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle restore version
   */
  const handleRestore = async (versionId: string) => {
    setRestoring(versionId);
    try {
      await onRestore(versionId);
      await loadVersionHistory(); // Reload to show new version
    } catch (err: any) {
      setError(err.message || 'Failed to restore version');
    } finally {
      setRestoring(null);
    }
  };

  /**
   * Toggle version details
   */
  const toggleVersionDetails = (versionId: string) => {
    setExpandedVersion(expandedVersion === versionId ? null : versionId);
  };

  /**
   * Get priority color
   */
  const getPriorityColor = (
    priority: NotificationPriority
  ): 'error' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case NotificationPriority.CRITICAL:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.NORMAL:
        return 'info';
      case NotificationPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Typography variant="h6">Version History</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {templateType && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {templateType}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!loading && !error && versions.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No version history available
            </Typography>
          </Box>
        )}

        {!loading && versions.length > 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Version history allows you to track changes over time and restore previous
                versions. The most recent version is shown at the top.
              </Typography>
            </Alert>

            <Timeline position="right">
              {versions.map((version, index) => (
                <TimelineItem key={version.id}>
                  <TimelineOppositeContent color="text.secondary" sx={{ flex: 0.3 }}>
                    <Typography variant="caption">
                      {format(version.changedAt, 'MMM d, yyyy')}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {format(version.changedAt, 'h:mm a')}
                    </Typography>
                  </TimelineOppositeContent>

                  <TimelineSeparator>
                    <TimelineDot color={index === 0 ? 'primary' : 'grey'}>
                      <HistoryIcon fontSize="small" />
                    </TimelineDot>
                    {index < versions.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>

                  <TimelineContent>
                    <Paper
                      elevation={index === 0 ? 3 : 1}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: index === 0 ? 2 : 1,
                        borderColor: index === 0 ? 'primary.main' : 'divider',
                      }}
                    >
                      {/* Version Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Version {version.version}
                        </Typography>
                        {index === 0 && <Chip label="Current" size="small" color="primary" />}
                        <Chip
                          label={version.priority}
                          size="small"
                          color={getPriorityColor(version.priority)}
                        />
                        <Chip
                          label={version.enabled ? 'Enabled' : 'Disabled'}
                          size="small"
                          color={version.enabled ? 'success' : 'default'}
                        />
                      </Box>

                      {/* Change Notes */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {version.changeNotes}
                      </Typography>

                      {/* Changed By */}
                      <Typography variant="caption" color="text.secondary">
                        Changed by {version.changedBy}
                      </Typography>

                      {/* Actions */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => toggleVersionDetails(version.id)}
                        >
                          {expandedVersion === version.id ? 'Hide' : 'View'} Details
                        </Button>
                        {index !== 0 && (
                          <Button
                            size="small"
                            startIcon={
                              restoring === version.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RestoreIcon />
                              )
                            }
                            onClick={() => handleRestore(version.id)}
                            disabled={restoring !== null}
                            color="warning"
                          >
                            Restore
                          </Button>
                        )}
                      </Box>

                      {/* Expanded Details */}
                      {expandedVersion === version.id && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Template Content
                          </Typography>

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Title:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {version.title}
                            </Typography>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Message:
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {version.message}
                            </Typography>
                          </Box>

                          {version.variables.length > 0 && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Variables ({version.variables.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {version.variables.map((variable) => (
                                  <Chip
                                    key={variable}
                                    label={`{{${variable}}}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontFamily: 'monospace' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
